/**
 * PATH: apps/quote/src/pages/pricelist-page/HarvestWizardModal.tsx
 * PURPOSE: "Build a pricelist from job quotes" wizard — select jobs → preview
 *          (dry-run) → commit drafts into the active pricebook.
 * WHY: Operator 2026-06-14 — "read and create a pricelist off all the quotes
 *      already inside Bravada, in their Excel upload in job." The harvest engine
 *      existed but had no selectable/preview UI; this surfaces it.
 * FLOW: getHarvestSources → select job_ids → harvestQuotes(dry_run) preview →
 *       harvestQuotes(commit) → drafts land in grid (is_active=false).
 * RELATED: pricing-api (getHarvestSources/harvestQuotes), PricelistPage
 */
import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { X, Loader2, FileSpreadsheet, ChevronRight, CheckCircle2 } from 'lucide-react';
import { quoteApi } from '@/lib/quoteApi';

interface HarvestWizardModalProps {
  open: boolean;
  pricebookId?: string;
  onClose: () => void;
  onCommitted: () => void;
}

type Step = 'select' | 'preview' | 'done';

export default function HarvestWizardModal({ open, pricebookId, onClose, onCommitted }: HarvestWizardModalProps) {
  const [step, setStep] = useState<Step>('select');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [excelFallback, setExcelFallback] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  const [committed, setCommitted] = useState<any>(null);

  const sourcesQuery = useQuery({
    queryKey: ['harvest-sources'],
    queryFn: () => quoteApi.getHarvestSources(),
    enabled: open,
  });

  const harvestMutation = useMutation({
    mutationFn: (dryRun: boolean) =>
      quoteApi.harvestQuotes({
        dry_run: dryRun,
        job_ids: selected.size ? Array.from(selected) : undefined,
        parse_excel_fallback: excelFallback,
        pricebook_id: pricebookId,
        min_occurrences: 1,
      }),
    onSuccess: (data, dryRun) => {
      if (dryRun) { setPreview(data); setStep('preview'); }
      else { setCommitted(data); setStep('done'); onCommitted(); }
    },
  });

  if (!open) return null;

  const jobs = sourcesQuery.data?.jobs ?? [];
  const allSelected = jobs.length > 0 && selected.size === jobs.length;
  const toggle = (id: number) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(jobs.map((j) => j.job_id)));
  const close = () => { setStep('select'); setSelected(new Set()); setPreview(null); setCommitted(null); onClose(); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={close}>
      <div className="w-full max-w-2xl rounded-xl bg-white dark:bg-gray-800 shadow-xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700 px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-purple-600" /> Build pricelist from job quotes
          </h2>
          <button onClick={close} className="p-1 text-gray-400 hover:text-gray-600 rounded"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {step === 'select' && (
            <SelectStep
              loading={sourcesQuery.isLoading}
              jobs={jobs}
              selected={selected}
              allSelected={allSelected}
              onToggle={toggle}
              onToggleAll={toggleAll}
              excelFallback={excelFallback}
              onExcelToggle={() => setExcelFallback((v) => !v)}
            />
          )}
          {step === 'preview' && <PreviewStep preview={preview} />}
          {step === 'done' && <DoneStep committed={committed} />}
        </div>

        <div className="flex items-center justify-between border-t border-gray-100 dark:border-gray-700 px-5 py-4">
          <p className="text-xs text-gray-500">
            {step === 'select' && (selected.size ? `${selected.size} job(s) selected` : 'No selection = all jobs')}
            {step === 'preview' && 'Drafts are added inactive — review and tick is_active in the grid to publish.'}
          </p>
          <div className="flex items-center gap-2">
            {step === 'preview' && (
              <button onClick={() => setStep('select')} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Back</button>
            )}
            {step === 'select' && (
              <button
                onClick={() => harvestMutation.mutate(true)}
                disabled={harvestMutation.isPending || sourcesQuery.isLoading}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {harvestMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />}
                Preview
              </button>
            )}
            {step === 'preview' && (
              <button
                onClick={() => harvestMutation.mutate(false)}
                disabled={harvestMutation.isPending || !pricebookId}
                title={!pricebookId ? 'Select a pricelist first' : undefined}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {harvestMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Commit drafts
              </button>
            )}
            {step === 'done' && (
              <button onClick={close} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">Done</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SelectStep({ loading, jobs, selected, allSelected, onToggle, onToggleAll, excelFallback, onExcelToggle }: any) {
  if (loading) return <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  if (!jobs.length) return <div className="py-12 text-center text-gray-500">No jobs with quotes found for this organisation.</div>;
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600 dark:text-gray-300">
        Pick the jobs to harvest pricing from (their quote line-items become draft pricelist rows). Leave all unchecked to harvest every job.
      </p>
      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
        <input type="checkbox" checked={allSelected} onChange={onToggleAll} /> Select all ({jobs.length})
      </label>
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700 max-h-72 overflow-y-auto">
        {jobs.map((j: any) => (
          <label key={j.job_id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer">
            <input type="checkbox" checked={selected.has(j.job_id)} onChange={() => onToggle(j.job_id)} />
            <span className="font-mono text-sm text-gray-900 dark:text-white">Job {j.job_id}</span>
            <span className="text-xs text-gray-500 ml-auto">
              {j.lines_total} lines · {j.quote_count} quote{j.quote_count === 1 ? '' : 's'}
              {j.sheets_with_excel ? ` · ${j.sheets_with_excel} xlsx` : ''}
            </span>
          </label>
        ))}
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
        <input type="checkbox" checked={excelFallback} onChange={onExcelToggle} />
        Also read raw Excel for quotes with no parsed lines (slower)
      </label>
    </div>
  );
}

function PreviewStep({ preview }: { preview: any }) {
  if (!preview) return null;
  const top = preview.top_products ?? [];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Quotes scanned', value: preview.quotesheets_with_lines ?? 0 },
          { label: 'Lines kept', value: preview.lines_kept ?? 0 },
          { label: 'Distinct products', value: preview.distinct_products ?? 0 },
        ].map((s) => (
          <div key={s.label} className="rounded-lg border border-gray-100 dark:border-gray-700 p-3">
            <p className="text-xs text-gray-500">{s.label}</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">{s.value}</p>
          </div>
        ))}
      </div>
      {top.length > 0 && (
        <div className="border border-gray-100 dark:border-gray-700 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700"><tr>
              <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Product</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Supplier</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-300">Seen</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {top.slice(0, 15).map((p: any, i: number) => (
                <tr key={i}>
                  <td className="px-3 py-2 text-gray-900 dark:text-white">{p.name}</td>
                  <td className="px-3 py-2 text-gray-500">{p.supplier || '—'}</td>
                  <td className="px-3 py-2 text-right text-gray-500">{p.occurrences}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DoneStep({ committed }: { committed: any }) {
  return (
    <div className="py-8 text-center space-y-3">
      <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto" />
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Drafts created</h3>
      <p className="text-gray-500 text-sm">
        {committed?.items_written ?? committed?.products ?? 0} draft item(s) added to the pricelist.
        Open the <span className="font-medium">Pricelist (Excel)</span> tab, review them, then tick <span className="font-mono">is_active</span> and Save to publish.
      </p>
    </div>
  );
}
